import React, { useContext, useMemo, useState } from "react";
import { Popover } from "antd";
import { DownloadIcon, Text } from "components/commons";
import { ExportList, ImportList } from "hooks/useDownloader";
import { VenueContext } from "contexts";
import lang from "translations";
import reportStatus from "enums/report-status";

// Small download icon (placed next to the language selector) that shows a badge
// with the number of in-flight/ready reports, and opens a popover with the same
// export/import lists that used to live in the always-open bottom-right panel.
//
// Visibility rule is the same one the old bottom-right panel used (see the
// deleted useEffect in useDownloader.js pre-SVEMS-888): only Manager,
// BackOffice, and FrontOffice ever saw this panel at all, and of those, only
// Manager/BackOffice saw the import list — FrontOffice saw exports only.
// useDownloader now exposes that as canViewDownloads/canViewImports so we
// reproduce it here instead of inventing a different RBAC check.
const DownloadStatus = () => {
  const venueContext = useContext(VenueContext) || {};
  const {
    downloader = {},
    downloaderList = [],
    downloaderImportList = [],
    canViewDownloads = true,
    canViewImports = true,
    handleClickReport,
    handleClearReport,
    handleClearImport,
  } = venueContext;
  const [visible, setVisible] = useState(false);

  const visibleImportList = canViewImports ? downloaderImportList : [];

  const badgeCount = useMemo(() => {
    const list = downloaderList || [];
    return list.filter(
      (item) => item.reportStatus === reportStatus.processing || item.reportStatus === reportStatus.done
    ).length;
  }, [downloaderList]);

  const content = useMemo(() => {
    const hasExports = downloaderList && downloaderList.length > 0;
    const hasImports = visibleImportList && visibleImportList.length > 0;

    if (!hasExports && !hasImports) {
      return (
        <div className="w-64 p-md">
          <Text color="text-gray" className="text-xs">
            {lang.noRecentDownloads || "No recent downloads."}
          </Text>
        </div>
      );
    }

    return (
      <div className="flex gap-4 items-end flex-col overflow-hidden max-h-75">
        <ExportList
          handleClearReport={handleClearReport}
          handleClickReport={handleClickReport}
          title={downloader.title}
          list={downloaderList}
        />
        <ImportList handleClear={handleClearImport} list={visibleImportList} />
      </div>
    );
  }, [downloaderList, visibleImportList, downloader.title, handleClearReport, handleClickReport, handleClearImport]);

  if (!canViewDownloads) return null;

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      visible={visible}
      onVisibleChange={setVisible}
      content={content}
      overlayClassName="p-0"
    >
      <div className="relative flex items-center justify-center cursor-pointer">
        <DownloadIcon className="text-gray-dark hover:text-pelorous" />
        {badgeCount > 0 && (
          <span className="absolute -top-2 -right-2 min-w-4 h-4 px-1 rounded-full bg-pelorous text-white text-xxs font-semibold flex items-center justify-center">
            {badgeCount}
          </span>
        )}
      </div>
    </Popover>
  );
};

export default DownloadStatus;
